import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import axios from 'axios'
import { toast } from 'sonner'
import { ArrowLeft, MapPin, Briefcase, Building2, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { EMPLOYMENT_LABELS, jobLocation, type PublicJob } from './CareersList'

type PublicJobDetail = PublicJob & {
  public_description: string
  public_responsibilities: string
  public_requirements: string
  benefits: string
  seo_title: string
  seo_description: string
  og_image_url: string
}

type ConsentVersion = {
  id: number
  purpose: string
  title: string
  body_text: string
  body_html: string
  required: boolean
  locale: string
}

export default function CareersJobDetail() {
  const { slug } = useParams<{ slug: string }>()

  const { data: job, isLoading, isError } = useQuery<PublicJobDetail>({
    queryKey: ['careers-job', slug],
    queryFn: async () => (await axios.get(`/careers/api/jobs/${slug}/`)).data,
  })
  const { data: consents } = useQuery<ConsentVersion[]>({
    queryKey: ['careers-consents'],
    queryFn: async () => (await axios.get('/careers/api/consent-versions/')).data,
  })

  // Form state
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [linkedin, setLinkedin] = useState('')
  const [coverLetter, setCoverLetter] = useState('')
  const [cv, setCv] = useState<File | null>(null)
  const [ticked, setTicked] = useState<Set<number>>(new Set())
  const [website, setWebsite] = useState('') // honeypot — must stay empty
  const [submitted, setSubmitted] = useState<string | null>(null)

  const submit = useMutation({
    mutationFn: async () => {
      const fd = new FormData()
      fd.append('website', website)
      fd.append('job_slug', slug!)
      fd.append('first_name', firstName)
      fd.append('last_name', lastName)
      fd.append('email', email)
      if (phone) fd.append('phone', phone)
      if (linkedin) fd.append('linkedin_url', linkedin)
      if (coverLetter) fd.append('cover_letter', coverLetter)
      if (cv) fd.append('cv', cv)
      ticked.forEach((id) => fd.append('consent_version_ids', String(id)))
      const r = await axios.post('/careers/api/apply/', fd)
      return r.data
    },
    onSuccess: (data) => setSubmitted(data.message || 'Application received.'),
    onError: (e: any) => {
      const d = e?.response?.data
      const msg =
        d?.errors?.cv?.[0] || d?.missing_consents?.length
          ? 'Please accept the required consent checkboxes.'
          : d?.detail || 'Submission failed. Please check the form and try again.'
      toast.error(typeof msg === 'string' ? msg : 'Submission failed.')
    },
  })

  if (isLoading) return <PageShell><p className="text-muted-foreground">Loading…</p></PageShell>
  if (isError || !job) {
    return (
      <PageShell>
        <p className="text-muted-foreground">This position is no longer available.</p>
        <Link to="/careers" className="mt-4 inline-block text-sm underline">View all open positions</Link>
      </PageShell>
    )
  }

  const requiredOk = (consents || []).filter((c) => c.required).every((c) => ticked.has(c.id))
  const canSubmit = firstName && lastName && email && cv && requiredOk && !submit.isPending

  return (
    <PageShell>
      <Link to="/careers" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline">
        <ArrowLeft className="h-4 w-4" /> All positions
      </Link>

      <h1 className="mt-4 text-3xl font-bold tracking-tight">{job.title}</h1>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
        {job.department_name && <span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{job.department_name}</span>}
        <span className="inline-flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" />{EMPLOYMENT_LABELS[job.employment_type] || job.employment_type}</span>
        <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{jobLocation(job)}</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-1">
        {job.tags.map((t) => <Badge key={t} variant="secondary">{t}</Badge>)}
      </div>

      {/* Recruiter-authored HTML; admin-controlled, not user-generated. */}
      <Section title="About the role" html={job.public_description} />
      <Section title="What you'll do" html={job.public_responsibilities} />
      <Section title="What we're looking for" html={job.public_requirements} />
      {job.benefits && (
        <section className="mt-8">
          <h2 className="text-lg font-semibold">Benefits</h2>
          <p className="prose prose-sm mt-2 max-w-none whitespace-pre-line text-foreground">{job.benefits}</p>
        </section>
      )}

      <hr className="my-10" />

      {submitted ? (
        <div className="rounded-lg border bg-accent/50 p-6 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-green-600" />
          <h2 className="mt-3 text-xl font-semibold">Application received</h2>
          <p className="mt-2 text-muted-foreground">{submitted}</p>
        </div>
      ) : (
        <form
          onSubmit={(e) => { e.preventDefault(); submit.mutate() }}
          className="space-y-4"
        >
          <h2 className="text-xl font-semibold">Apply for this position</h2>

          {/* Honeypot — visually hidden, real users never fill it. */}
          <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off"
              value={website} onChange={(e) => setWebsite(e.target.value)} />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="first_name">First name *</Label>
              <Input id="first_name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="last_name">Last name *</Label>
              <Input id="last_name" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="linkedin">LinkedIn URL</Label>
              <Input id="linkedin" type="url" placeholder="https://linkedin.com/in/…" value={linkedin} onChange={(e) => setLinkedin(e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="cv">CV / Résumé (PDF or Word) *</Label>
              <Input id="cv" type="file" accept=".pdf,.doc,.docx"
                onChange={(e) => setCv(e.target.files?.[0] || null)} />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="cover">Cover letter</Label>
              <textarea id="cover" rows={5} maxLength={10000}
                className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={coverLetter} onChange={(e) => setCoverLetter(e.target.value)} />
            </div>
          </div>

          {(consents || []).length > 0 && (
            <div className="space-y-3 rounded-lg border p-4">
              {(consents || []).map((c) => (
                <div key={c.id} className="flex items-start gap-3">
                  <Checkbox id={`consent-${c.id}`} checked={ticked.has(c.id)}
                    onCheckedChange={(v) => {
                      const next = new Set(ticked)
                      v ? next.add(c.id) : next.delete(c.id)
                      setTicked(next)
                    }} />
                  <label htmlFor={`consent-${c.id}`} className="text-sm leading-snug">
                    <span className="font-medium">{c.title}{c.required && ' *'}</span>
                    <span className="mt-0.5 block text-muted-foreground">{c.body_text}</span>
                  </label>
                </div>
              ))}
            </div>
          )}

          <Button type="submit" disabled={!canSubmit} className="w-full sm:w-auto">
            {submit.isPending ? 'Submitting…' : 'Submit application'}
          </Button>
          {!requiredOk && (consents || []).length > 0 && (
            <p className="text-xs text-muted-foreground">* required consents must be accepted before submitting.</p>
          )}
        </form>
      )}
    </PageShell>
  )
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background">
      <main className="relative mx-auto max-w-3xl px-4 py-10">{children}</main>
    </div>
  )
}

function Section({ title, html }: { title: string; html: string }) {
  if (!html) return null
  return (
    <section className="mt-8">
      <h2 className="text-lg font-semibold">{title}</h2>
      <div className="prose prose-sm mt-2 max-w-none text-foreground" dangerouslySetInnerHTML={{ __html: html }} />
    </section>
  )
}
